import type {
  AdminCreateOrganizationDto,
  AdminSetMemberRoleDto,
  CompanySelectDto,
  IntelOrgAddMemberByEmailDto,
  IntelOrgAddMemberDto,
  IntelOrgMemberCandidatesResponseDto,
  IntelOrgMemberDetailDto,
  IntelOrgMemberSelectDto,
  IntelOrgSetPersonaDto,
  InviteSelectDto,
  UserCompanyNestedSelectDto,
} from '@@types/api.types';
import { type ApiErrorWrapper, useApi } from '@hooks/api.hook';
import { queryKeys } from '@queries/keys';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGetUserSelectedCompanyId } from './users.query';

// AZ-451: Platform-Admin org/membership management (cross-org) on /admin/organizations.

const MEMBER_CANDIDATES_PAGE_SIZE = 25;

// AZ-451 D9: paginated + debounced-search candidate list for the admin add-member picker. Each page is
// server-filtered to approved Intelligence users not already in the org; ineligible candidates
// (already in another Intelligence org) come back with `eligible: false` so the picker can disable them.
export const useAdminIntelMemberCandidates = (params: { companyId: string; search: string }) => {
  const { companyId, search } = params;
  const { apiGet } = useApi();

  return useInfiniteQuery({
    enabled: Boolean(companyId),
    getNextPageParam: (lastPage: IntelOrgMemberCandidatesResponseDto) =>
      lastPage.pagination.hasNextPage ? lastPage.pagination.page + 1 : undefined,
    initialPageParam: 1,
    placeholderData: keepPreviousData,
    queryFn: async ({ pageParam }) =>
      await apiGet<IntelOrgMemberCandidatesResponseDto>({
        path: `/v1/admin/companies/${companyId}/member-candidates`,
        query: { limit: MEMBER_CANDIDATES_PAGE_SIZE, page: pageParam, search: search || undefined },
      }),
    queryKey: queryKeys.adminCompanies.getMemberCandidates(companyId, search),
  });
};

export const useAdminCreateOrganization = () => {
  const { apiPost } = useApi({ showToastOnError: false });
  const queryClient = useQueryClient();

  return useMutation<CompanySelectDto, ApiErrorWrapper, AdminCreateOrganizationDto>({
    mutationFn: async (data) => await apiPost<CompanySelectDto>({ body: data, path: '/v1/admin/companies' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.root });
    },
  });
};

export const useAdminAddOrganizationMember = () => {
  const { apiPost } = useApi({ showToastOnError: false });
  const queryClient = useQueryClient();

  return useMutation<IntelOrgMemberSelectDto, ApiErrorWrapper, { companyId: string; data: IntelOrgAddMemberDto }>({
    mutationFn: async ({ companyId, data }) =>
      await apiPost<IntelOrgMemberSelectDto>({ body: data, path: `/v1/admin/companies/${companyId}/members` }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.root });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.getCompanyById(variables.companyId) });
    },
  });
};

export const useAdminSetOrganizationMemberPersona = () => {
  const { apiPatch } = useApi({ showToastOnError: false });
  const queryClient = useQueryClient();

  return useMutation<
    IntelOrgMemberSelectDto,
    ApiErrorWrapper,
    { companyId: string; data: IntelOrgSetPersonaDto; userCompanyId: string }
  >({
    mutationFn: async ({ companyId, data, userCompanyId }) =>
      await apiPatch<IntelOrgMemberSelectDto>({
        body: data,
        path: `/v1/admin/companies/${companyId}/members/${userCompanyId}`,
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.root });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.getCompanyById(variables.companyId) });
    },
  });
};

export const useAdminRemoveOrganizationMember = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, ApiErrorWrapper, { companyId: string; userCompanyId: string }>({
    mutationFn: async ({ companyId, userCompanyId }) => {
      await apiDelete({ path: `/v1/admin/companies/${companyId}/members/${userCompanyId}` });
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.root });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.getCompanyById(variables.companyId) });
    },
  });
};

// Non-Intelligence orgs: update a member's generic company role (Admin/Collaborator).
export const useAdminUpdateOrganizationMemberRole = () => {
  const { apiPatch } = useApi({ showToastOnError: false });
  const queryClient = useQueryClient();

  return useMutation<
    UserCompanyNestedSelectDto,
    ApiErrorWrapper,
    { companyId: string; data: AdminSetMemberRoleDto; userCompanyId: string }
  >({
    mutationFn: async ({ companyId, data, userCompanyId }) =>
      await apiPatch<UserCompanyNestedSelectDto>({
        body: data,
        path: `/v1/admin/companies/${companyId}/members/${userCompanyId}/role`,
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanies.getCompanyById(variables.companyId) });
    },
  });
};

// AZ-451: Program-Admin own-org membership management on /organization (selected org context).

export const useIntelOrganizationMembersQuery = (enabled = true) => {
  const { apiGet } = useApi();
  const selectedCompanyId = useGetUserSelectedCompanyId();

  return useQuery<IntelOrgMemberDetailDto[], ApiErrorWrapper>({
    enabled: enabled && Boolean(selectedCompanyId),
    queryFn: async () => await apiGet<IntelOrgMemberDetailDto[]>({ path: '/v1/intel/organization/members' }),
    queryKey: queryKeys.intelOrganization.members(selectedCompanyId),
  });
};

export const useAddIntelOrganizationMember = () => {
  const { apiPost } = useApi({ showToastOnError: false });
  const queryClient = useQueryClient();

  return useMutation<InviteSelectDto, ApiErrorWrapper, IntelOrgAddMemberByEmailDto>({
    mutationFn: async (data) => await apiPost<InviteSelectDto>({ body: data, path: '/v1/intel/organization/members' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.intelOrganization.root });
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.getCurrentUser });
    },
  });
};

export const useSetIntelOrganizationMemberPersona = () => {
  const { apiPatch } = useApi({ showToastOnError: false });
  const queryClient = useQueryClient();

  return useMutation<IntelOrgMemberSelectDto, ApiErrorWrapper, { data: IntelOrgSetPersonaDto; userCompanyId: string }>({
    mutationFn: async ({ data, userCompanyId }) =>
      await apiPatch<IntelOrgMemberSelectDto>({ body: data, path: `/v1/intel/organization/members/${userCompanyId}` }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.intelOrganization.root });
    },
  });
};

export const useRemoveIntelOrganizationMember = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, ApiErrorWrapper, string>({
    mutationFn: async (userCompanyId) => {
      await apiDelete({ path: `/v1/intel/organization/members/${userCompanyId}` });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.intelOrganization.root });
    },
  });
};
